import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";
import { MatchPill } from "../components/ListingCard";
import { SourceBadge } from "../components/SourceBadge";
import { Button } from "../components/ui/button";
import { DetailGallery } from "../components/detail/DetailGallery";
import { DetailMatchBlock } from "../components/detail/DetailMatchBlock";
import { DetailFacts } from "../components/detail/DetailFacts";
import { PlanViewingDialog } from "../components/detail/PlanViewingDialog";

const WoningDetail = () => {
  const { id } = useParams();
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(false);
  const [fav, setFav] = useState(false);

  const jsonLd = listing
    ? {
        "@context": "https://schema.org",
        "@type": "Apartment",
        name: listing.title,
        description: listing.description,
        image: listing.gallery && listing.gallery.length ? listing.gallery : listing.image ? [listing.image] : undefined,
        numberOfBedrooms: listing.bedrooms,
        floorSize: { "@type": "QuantitativeValue", value: listing.m2, unitCode: "MTK" },
        address: {
          "@type": "PostalAddress",
          streetAddress: listing.street,
          addressLocality: listing.city,
          addressCountry: "NL",
        },
        offers: {
          "@type": "Offer",
          price: listing.price,
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
        },
      }
    : null;

  useSeo({
    title: listing ? listing.title : undefined,
    description: listing?.description ? listing.description.slice(0, 155) : undefined,
    image: (listing && (listing.gallery?.[0] || listing.image)) || "/og-image.png",
    jsonLd,
  });

  useEffect(() => {
    setListing(null);
    setError(false);
    api(`/listings/${id}`)
      .then((d) => {
        setListing(d);
        setFav(!!d.is_favorite);
      })
      .catch(() => setError(true));
  }, [id]);

  const toggleFav = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      if (fav) {
        await api(`/favorites/${id}`, { method: "DELETE" });
        setFav(false);
      } else {
        await api("/favorites", { method: "POST", body: JSON.stringify({ listing_id: id }) });
        setFav(true);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const respond = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      await api(`/listings/${id}/respond`, {
        method: "POST",
        body: JSON.stringify({ external_url: listing.source_url || null }),
      });
      if (listing.source_url) {
        window.open(listing.source_url, "_blank", "noopener");
      } else {
        toast.success(t("detail.responded"));
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <p className="text-slate-500">{t("detail.notfound")}</p>
        <Button asChild className="mt-6 rounded-xl">
          <Link to="/woningen">{t("nav.woningen")}</Link>
        </Button>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="aspect-[16/9] w-full animate-pulse rounded-3xl bg-slate-100" />
        <div className="mt-6 h-8 w-1/2 animate-pulse rounded bg-slate-100" />
        <div className="mt-3 h-4 w-1/3 animate-pulse rounded bg-slate-100" />
      </div>
    );
  }

  const gallery = listing.gallery && listing.gallery.length ? listing.gallery : listing.image ? [listing.image] : [];

  return (
    <div className="pb-28">
      <div className="mx-auto max-w-5xl px-4 pt-6 sm:px-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="detail-back"
          className="mb-4 flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-navy"
        >
          <ArrowLeft size={16} /> {t("common.back")}
        </button>

        <DetailGallery gallery={gallery} title={listing.title} fav={fav} onToggleFav={toggleFav} />

        {/* Title */}
        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="detail-title">
              {listing.title}
            </h1>
            <p className="mt-1 flex items-center gap-1 text-slate-500">
              <MapPin size={15} /> {listing.street}, {listing.neighborhood}, {listing.city}
            </p>
            <div className="mt-2">
              <SourceBadge source={listing.source} />
            </div>
          </div>
          {typeof listing.match_score === "number" && <MatchPill score={listing.match_score} className="text-sm" />}
        </div>

        <DetailMatchBlock listing={listing} user={user} />

        <DetailFacts listing={listing} />

        {/* Description */}
        {listing.description && (
          <>
            <h2 className="mt-8 font-display text-xl font-bold text-navy">{t("detail.description")}</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-600">{listing.description}</p>
          </>
        )}

        {/* Scam hint */}
        <Link
          to="/scam-check"
          className="mt-8 flex items-center gap-3 rounded-2xl border border-border bg-slate-50 p-4 transition-colors hover:bg-brandblue-light/50"
        >
          <ShieldCheck size={22} className="text-success" />
          <span className="text-sm font-medium text-navy">{t("detail.scamHint")}</span>
        </Link>

        <PlanViewingDialog listingId={id} user={user} navigate={navigate} />

        {/* Map */}
        {listing.lat && listing.lng && (
          <>
            <h2 className="mt-8 font-display text-xl font-bold text-navy">{t("detail.location")}</h2>
            <div className="mt-4 overflow-hidden rounded-3xl border border-border">
              <iframe
                title="map"
                data-testid="detail-map"
                className="h-72 w-full"
                loading="lazy"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${listing.lng - 0.01}%2C${
                  listing.lat - 0.007
                }%2C${listing.lng + 0.01}%2C${listing.lat + 0.007}&layer=mapnik&marker=${listing.lat}%2C${listing.lng}`}
              />
            </div>
          </>
        )}

        {/* Spacer so the sticky bottom CTA never covers content */}
        <div className="h-28" aria-hidden="true" />
      </div>

      {/* Sticky bottom CTA — safe-area aware for iOS */}
      <div className="fixed inset-x-0 bottom-0 z-40 glass border-t border-border pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <div className="truncate font-display text-lg font-extrabold text-navy sm:text-xl">
              {formatPrice(listing.price)}
              <span className="text-xs font-medium text-slate-400 sm:text-sm"> {t("common.perMonth")}</span>
            </div>
          </div>
          <Button onClick={respond} data-testid="detail-respond" className="h-12 shrink-0 rounded-full bg-brandblue px-6 text-sm font-semibold hover:bg-brandblue/90 sm:px-8 sm:text-base">
            {t("detail.apply")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WoningDetail;
